'use client';

//@ts-ignore
import 'ag-grid-community/styles/ag-grid.css'; // Mandatory CSS required by the grid

//@ts-ignore
import 'ag-grid-community/styles/ag-theme-quartz.css'; // Optional Theme applied to the grid

import { cn, randomuuid } from '@/lib/utils';
import { AgGridReact } from 'ag-grid-react'; // React Data Grid Component
import { useTranslations } from 'next-intl';
import { useTheme } from 'next-themes';
import {
    forwardRef,
    Fragment,
    memo,
    PropsWithChildren,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import CustomLoading from '../custom-loading';
import TableAdvanceSearch from './components/table-ag-advance-search';
import TableAgRowSelected, {
    TableAGRowSelectedRefs,
} from './components/table-ag-row-selected';
import TableFilterAction from './components/table-filter-action';
import TablePagination from './components/table-pagination';
import { gridOptions } from './table-ag-constants';
import {
    FilterChangedEvent,
    GridApi,
    GridReadyEvent,
    SelectionChangedEvent,
    TableAGProps,
    TableAGRef,
} from './table-ag-types';
import { useTableAGStore } from './use-table-ag';

const NoRowsOverlayCmp = (t: any, notFoundData?: boolean) => (
    <div className='flex flex-col items-center justify-center'>
        <span className='text-base font-medium'>
            {t(notFoundData ? 'System_NoDataFound' : 'System_NoRowsData')}.
        </span>
    </div>
);

const LoadingOverlayCmp = () => <CustomLoading.Round className='h-52' />;

const CustomTableAG = forwardRef<TableAGRef, PropsWithChildren<TableAGProps>>(
    (props, ref) => {
        const {
            wrapperClassName,
            rowData = undefined,
            queryData,
            pageInfo = {},
            setPageInfo,
            columnDefs,
            suppressRowClickSelection = false,
            hidePagination = false,
            showTotalRowSelected = true,
            isLoading,
            filterAction,
            sortList,
            children,

            renderAdvanceSearch,
            rowMultiSelectWithClick = true,
            domLayout = 'autoHeight',
            tableHeight,
            animateRows,
            showCheckbox = true,
            selectMode,
            suppressHorizontalScroll,
            extendGridOptions,

            onColumnVisibilityChange,
            setResizeTableStatus,
            onRowDataUpdated,
            onSelectionChanged,
            onFilterChanged,

            ...rest
        } = props;

        const transDef = useTranslations('System');

        /**table unique-key */
        const _uniqueKey = useRef<string>(randomuuid());
        const renderCountBottomTableRef = useRef<TableAGRowSelectedRefs>(null);
        const _gridApi = useRef<GridApi<any>>();
        const { theme } = useTheme();

        const setTableAGState = useTableAGStore((x) => x.setTableAGState);

        const [isAGGridReady, setIsAGGridReady] = useState(false);
        // Row Data: The data to be displayed.
        const [tableData, setTableData] = useState(rowData);
        // resize status
        const [resizeStatus, setResizeStatus] = useState<boolean>(
            typeof window !== 'undefined' ? window?.innerWidth <= 1024 : false
        );
        //
        const [isDoneAfterFirstDataRender, setIsDoneAfterFirstDataRender] =
            useState(false);

        /**callback Resize table column  */
        const _resizeTable = useCallback(
            (isFull: boolean) => {
                if (_gridApi.current) {
                    const allColumnIds: string[] = [];
                    const cols = _gridApi.current?.getColumns();

                    if (cols) {
                        cols.forEach((column: any) => {
                            allColumnIds.push(column.getId());
                            // colsWidth += column.getActualWidth()
                        });
                    }

                    if (!isFull) {
                        _gridApi.current.sizeColumnsToFit();
                    } else {
                        _gridApi.current.autoSizeColumns(allColumnIds, false);
                    }

                    setResizeStatus(isFull ?? false);
                    setResizeTableStatus?.(isFull ?? false);
                }
            },
            [setResizeTableStatus]
        );

        /**Trigger table loading */
        const triggerTableLoading = useCallback(() => {
            if (_gridApi.current) {
                _gridApi.current.setGridOption?.('loading', true);
            }
        }, []);

        /**
         * resize timeout
         */
        const timeout_resize = useRef<NodeJS.Timeout>();
        const _handleResizeWhenChangeWindow = useCallback(() => {
            clearTimeout(timeout_resize.current);
            timeout_resize.current = setTimeout(() => {
                _resizeTable(false);
            }, 300);
        }, [_resizeTable]);

        /**
         * Set rows data
         */
        useEffect(() => {
            if (rowData && Array.isArray(rowData)) {
                setTableData(rowData);
            }
        }, [rowData]);

        /**
         * Show/Hide overlay loading, no rows
         */
        useEffect(() => {
            if (_gridApi.current) {
                if (isLoading) {
                    if (!tableData) {
                        _gridApi.current.setGridOption('rowData', []);
                    }
                    _gridApi.current.updateGridOptions?.({ loading: true });
                } else {
                    if (!tableData) {
                        if (
                            !queryData?.data?.Items ||
                            queryData?.data?.Items?.length === 0
                        ) {
                            _gridApi.current.showNoRowsOverlay?.();
                        } else {
                            _gridApi.current.updateGridOptions?.({
                                loading: false,
                            });
                        }
                    }
                    _gridApi.current.updateGridOptions?.({ loading: false });
                }
            }
            // don't remove isAGGridReady
        }, [isLoading, queryData, isAGGridReady, tableData]);

        /**
         * handle resize
         */
        useEffect(() => {
            _handleResizeWhenChangeWindow();
            window.addEventListener('resize', _handleResizeWhenChangeWindow);
            return () => {
                window.removeEventListener(
                    'resize',
                    _handleResizeWhenChangeWindow
                );
            };
        }, [_handleResizeWhenChangeWindow]);

        /**
         * clear table ref in store
         */
        useEffect(() => {
            return () => {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                let _key = _uniqueKey.current;
                setTableAGState((st) => ({
                    tableAGRefs: {
                        ...(st?.tableAGRefs ?? {}),
                        [_key]: undefined,
                    },
                }));
            };
        }, [setTableAGState]);

        /**
         * Handle when table ready
         */
        const onGridReadyHandler = useCallback(
            (event: GridReadyEvent) => {
                if (event.api) {
                    _gridApi.current = event.api;

                    event.api.setFilterModel({});
                    event.api.setGridOption('domLayout', domLayout);

                    _resizeTable(resizeStatus);
                    setIsAGGridReady(true);

                    if (_gridApi.current) {
                        setTableAGState((st) => ({
                            tableAGRefs: {
                                ...(st?.tableAGRefs ?? {}),
                                [_uniqueKey.current]: _gridApi.current,
                            },
                        }));
                    }
                }
            },
            [domLayout, _resizeTable, resizeStatus, setTableAGState]
        );

        /**
         * ref
         */
        useImperativeHandle(ref, () => {
            return {
                _gridApi: _gridApi.current,
                getGridApi: () => _gridApi.current,
                showHideColumn(columnDefinations) {
                    _gridApi.current?.setGridOption(
                        'columnDefs',
                        columnDefinations
                    );
                },
                showLoadingOverlay() {
                    _gridApi.current?.setGridOption('loading', true);
                },
                showNoRowOverlay() {
                    _gridApi.current?.showNoRowsOverlay();
                },
                hideAllOverlay() {
                    _gridApi.current?.hideOverlay();
                },
                resizeStatus,
                _resizeTable,
            };
        }, [_resizeTable, resizeStatus]);

        const onClearSelection = useCallback(() => {
            if (_gridApi.current) {
                _gridApi.current.deselectAll?.();
            }
        }, []);

        const onSelectionChangedHandler = useCallback(
            (e: SelectionChangedEvent<any, any>) => {
                if (renderCountBottomTableRef.current) {
                    renderCountBottomTableRef.current.onUpdateRowSelected(
                        e.api.getSelectedNodes() ?? []
                    );
                }
                onSelectionChanged?.(e);
            },
            [onSelectionChanged]
        );

        const onFilterChangedHandler = useCallback(
            (e: FilterChangedEvent<any, any>) => {
                onFilterChanged?.(e);

                const hvDisplay = e.api.isAnyFilterPresent();
                renderCountBottomTableRef.current?.onUpdateRowDisplayed(
                    hvDisplay ? e.api.getDisplayedRowCount() : 0
                );
            },
            [onFilterChanged]
        );

        const onColumnVisibilityChangeHandler = useCallback(() => {
            onColumnVisibilityChange?.(_gridApi?.current?.getColumnDefs());
        }, [onColumnVisibilityChange]);

        const renderFilterAction = useMemo(() => {
            if (!filterAction) return <Fragment />;
            const searchCmp = (
                <TableFilterAction
                    callback={filterAction?.onSearch}
                    inputProps={filterAction?.searchProps}
                />
            );
            return filterAction.render(searchCmp);
        }, [filterAction]);

        const { TotalCount, TotalPages, dataShowOnTable } = useMemo(() => {
            const { Items, TotalCount, TotalPages } = queryData?.data ?? {};
            const dataShowOnTable = Items ?? tableData;

            return {
                TotalCount,
                TotalPages,
                dataShowOnTable,
            };
        }, [queryData?.data, tableData]);

        return (
            <div
                className={cn(
                    'my-2 min-h-10 [&_.ag-overlay]:z-[1]',
                    wrapperClassName
                )}
            >
                {/* render filter */}
                {isAGGridReady && renderFilterAction}
                {renderAdvanceSearch && (
                    <TableAdvanceSearch
                        renderAdvanceSearch={renderAdvanceSearch}
                    />
                )}

                <div style={{ width: '100%', height: tableHeight ?? '100%' }}>
                    {/* wrapping container with theme & size */}
                    <div
                        className={cn(
                            'ag-theme-quartz h-full w-full max-w-full [&_.ag-root]:min-w-full',
                            {
                                'ag-theme-custom-dark': theme === 'dark',
                            }
                        )} // applying the grid theme
                    >
                        <AgGridReact
                            gridOptions={{
                                ...gridOptions,
                                ...(extendGridOptions ?? {}),
                            }}
                            onGridReady={onGridReadyHandler}
                            rowData={isLoading ? [] : dataShowOnTable}
                            columnDefs={columnDefs as any}
                            popupParent={
                                typeof window !== 'undefined'
                                    ? document.body
                                    : undefined
                            }
                            rowSelection={{
                                mode: selectMode ?? 'multiRow',
                                enableClickSelection:
                                    !suppressRowClickSelection,
                                enableMultiSelectWithClick:
                                    rowMultiSelectWithClick,

                                //Show/Hide checkbox
                                checkboxes: showCheckbox,
                                headerCheckbox: showCheckbox,
                                //
                                selectAll: 'filtered',
                            }} // upgrade lib
                            animateRows={animateRows}
                            loadingOverlayComponent={LoadingOverlayCmp}
                            // tooltipShowDelay={0}
                            tooltipHideDelay={2000}
                            deltaSort
                            tooltipMouseTrack
                            enableBrowserTooltips
                            onNewColumnsLoaded={() => {
                                _resizeTable(resizeStatus);
                            }}
                            onRowDataUpdated={(e) => {
                                onRowDataUpdated?.(e);
                            }}
                            onFirstDataRendered={(e) => {
                                setTimeout(() => {
                                    _resizeTable(resizeStatus);
                                    setIsDoneAfterFirstDataRender(true);
                                }, 400);
                            }}
                            tabToNextCell={(params) => false}
                            onSelectionChanged={onSelectionChangedHandler}
                            onColumnVisible={onColumnVisibilityChangeHandler}
                            onFilterChanged={onFilterChangedHandler}
                            noRowsOverlayComponent={() => {
                                //?Hiển không có dữ liệu đối với trường hợp filter
                                const hvFilter =
                                    pageInfo.filters &&
                                    pageInfo.filters.length > 0;
                                return NoRowsOverlayCmp(transDef, hvFilter);
                            }}
                            {...(rest as any)}
                            pagination={false}
                            suppressHorizontalScroll={
                                !isDoneAfterFirstDataRender ||
                                suppressHorizontalScroll
                            }
                        />
                    </div>
                </div>

                {/* Khi nào table-ag ready thì mới render */}
                {isAGGridReady &&
                    !(
                        (
                            hidePagination && // Điều kiện ẩn pagination
                            (suppressRowClickSelection || showCheckbox)
                        ) // Điều kiện hiện checkbox hoặc hiện và disabled click
                    ) && (
                        <div className='my-2 flex flex-row flex-wrap justify-between gap-6 gap-y-2'>
                            <TableAgRowSelected
                                ref={renderCountBottomTableRef}
                                onClearRowSelected={onClearSelection}
                            />

                            {hidePagination ? null : (
                                <TablePagination
                                    current={pageInfo.pageNumber}
                                    pageSize={pageInfo.pageSize}
                                    queryFn_totalPage={TotalPages}
                                    queryFn_totalCount={TotalCount}
                                    showPageSize
                                    callback={setPageInfo}
                                    showTableLoading={triggerTableLoading}
                                    isHvError={
                                        queryData && !queryData?.isSuccessStatus
                                    }
                                />
                            )}
                        </div>
                    )}

                {children}
            </div>
        );
    }
);

CustomTableAG.displayName = 'TableAG';
export default memo(CustomTableAG);
